/**
 * FleetEnrollForm — "Zařadit do půjčovny" create surface (`/pujcovna/vozidla/nove`). One full page
 * (AGENTS.md editing pattern): pick the stock car, then its rental settings (the body shared with the edit
 * modal). Needs `pujcovna:write`, like the API. `?vin=` preselects a car (the link on a car's detail).
 * Everything the server decides (clear owner, lessor details of the owner, one entry per VIN) comes back
 * as a 409 message and is shown as it is.
 */

import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { AppRail } from '@/components/AppRail';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Button } from '@/components/Button';
import { Crown } from '@/components/Crown';
import { DisplayHeading } from '@/components/DisplayHeading';
import { EmptyState } from '@/components/EmptyState';
import { FormPanel, SectionHeader } from '@/components/FormPanel';
import { Icon } from '@/components/Icon';
import { Savebar } from '@/components/Savebar';
import { Shell } from '@/components/Shell';
import { Skeleton } from '@/components/Skeleton';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { useModuleAccess } from '@/lib/permissions';
import {
  useEnrollFleetMutation,
  useFleetQuery,
  useKategorieNabidkaQuery,
  usePobockyQuery,
  useTarifyQuery,
  type DirectoryVehicle,
} from './api';
import { carTitle, FleetCarPicker } from './FleetCarPicker';
import { FLEET_FORM_ID, FleetFormBody, fleetSeed, fleetToBody, type FleetValue } from './fleet-section-forms';
import type { FleetEnrollSearch } from './helpers';

export function FleetEnrollForm() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/pujcovna/vozidla/nove' }) as FleetEnrollSearch;
  const { can, resolved } = useModuleAccess();
  const allowed = resolved && can('pujcovna', 'write');
  const enroll = useEnrollFleetMutation();
  const pobocky = usePobockyQuery();
  const tarify = useTarifyQuery(allowed);
  const kategorie = useKategorieNabidkaQuery(allowed);
  const fleet = useFleetQuery(allowed);
  const saving = enroll.isPending;
  // Same rule as the edit modal: the form waits for its option lists, and a failed one blocks the submit
  // (otherwise a not-yet-loaded tarif list would silently enrol the car without one).
  const lookups = [pobocky, tarify, kategorie];
  // `isPending`, not `isLoading`: tarify / kategorie are gated on membership, and a gated query that has
  // not started is pending too — the form must not show "ready" with empty option lists before that.
  const lookupsLoading = lookups.some((q) => q.isPending && !q.isError);
  const lookupsFailed = lookups.some((q) => q.isError);

  const [car, setCar] = useState<DirectoryVehicle | null>(null);
  const pick = useCallback((c: DirectoryVehicle | null) => setCar(c), []);

  // null until the fleet has answered (the deep-link preselection waits for it); a failed fleet read
  // counts as none enrolled — the server still refuses a car that is.
  const enrolledVins = useMemo(
    () =>
      fleet.isSuccess || fleet.isError
        ? new Set((fleet.data ?? []).map((e) => e.vin.replace(/\s+/g, '').toUpperCase()))
        : null,
    [fleet.isSuccess, fleet.isError, fleet.data],
  );

  // Stable seed: the first pobočka (the list is ordered, the first is the default) once they load.
  const firstPobocka = pobocky.data?.[0]?.id ?? null;
  const seed = useMemo(() => fleetSeed(null, firstPobocka), [firstPobocka]);

  const back = () => navigate({ to: '/pujcovna/vozidla' });

  const submit = (form: FleetValue) => {
    if (!car) {
      toast.error('Vyberte vozidlo.');
      return;
    }
    if (!form.id_pobocky) {
      toast.error('Vyberte pobočku.');
      return;
    }
    const body = fleetToBody(form);
    if (!body.ok) {
      toast.error(body.chyba);
      return;
    }
    enroll.mutate(
      { zdroj_typ: car.kind, id_zdroje: car.id_vozidla, ...body.body },
      {
        onSuccess: () => {
          toast.ok('Vozidlo zařazeno do půjčovny.');
          back();
        },
        onError: (err) => toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se zařadit vozidlo.'}`),
      },
    );
  };

  return (
    <Shell rail={<AppRail active="pujcovna" />}>
      <main className="flex min-w-0 flex-col overflow-auto">
        <Crown>
          <Breadcrumb
            segments={[
              { label: 'Půjčovna', href: '/pujcovna' },
              { label: 'Vozidla', href: '/pujcovna/vozidla' },
              { label: 'Zařadit vozidlo' },
            ]}
          />
          <Crown.Row className="mt-3">
            <DisplayHeading>
              Zařadit vozidlo <em>do půjčovny.</em>
            </DisplayHeading>
          </Crown.Row>
        </Crown>

        {!resolved ? (
          // The page renders before membership resolves: nothing below may query or offer a choice yet.
          <div className="flex flex-col gap-2 px-8 py-8">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : !allowed ? (
          <div data-testid="fleet-form-denied" className="px-8 pb-8 pt-5">
            <EmptyState
              eyebrow="403"
              title="Vozidla do půjčovny zařazuje člen s právem měnit data půjčovny."
              description="Pro přístup požádejte správce firmy."
            />
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-6 px-8 py-8">
              <FormPanel>
                <SectionHeader
                  number={1}
                  title={<>Které <em>vozidlo</em>.</>}
                  description="Půjčovna využívá vůz ze skladu, nevytváří vlastní kartu. Vlastník vozu musí mít vyplněný název, IČO a bankovní účet."
                />
                <FleetCarPicker value={car} onChange={pick} initialVin={search.vin} enrolledVins={enrolledVins} disabled={saving} />
              </FormPanel>
              <FormPanel>
                <SectionHeader
                  number={2}
                  title={<>Nastavení <em>v půjčovně</em>.</>}
                  description="Pobočka, ze které se vůz půjčuje, tarif a kategorie pro web. Půjde to kdykoli upravit."
                />
                {lookupsFailed ? (
                  <p data-testid="fleet-lookups-error" className="text-[13px] text-ink-2">
                    Pobočky, tarify nebo kategorie se nepodařilo načíst. Zkuste to prosím znovu za chvíli.
                  </p>
                ) : lookupsLoading ? (
                  <div className="flex flex-col gap-2">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ) : (pobocky.data ?? []).length === 0 ? (
                  <p data-testid="fleet-no-pobocka" className="text-[13px] text-ink-2">
                    Nejdřív založte pobočku ve Správě půjčovny.
                  </p>
                ) : (
                  <FleetFormBody
                canAdmin={resolved && can('pujcovna', 'admin')}
                    initial={seed}
                    busy={saving}
                    pobocky={pobocky.data ?? []}
                    tarify={tarify.data ?? []}
                    kategorie={kategorie.data ?? []}
                    onSubmit={submit}
                  />
                )}
              </FormPanel>
            </div>

            <Savebar
              meta={[
                { k: 'Režim', v: 'Nový záznam' },
                { k: 'Vozidlo', v: car ? carTitle(car) : '—' },
              ]}
              actions={
                <>
                  <Button variant="secondary" icon={<Icon name="chevronL" size={12} />} onClick={back} disabled={saving}>
                    Zrušit
                  </Button>
                  <Button
                    variant="ink"
                    type="submit"
                    form={FLEET_FORM_ID}
                    disabled={saving || !car || lookupsLoading || lookupsFailed || (pobocky.data ?? []).length === 0}
                  >
                    Zařadit do půjčovny
                  </Button>
                </>
              }
            />
          </>
        )}
      </main>
    </Shell>
  );
}
